import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, api } from "../App";
import { ArrowLeft, Check, Crown, Sparkles, Users, Heart, Bitcoin, Copy, RefreshCw, GraduationCap } from "lucide-react";
import { Button } from "../components/ui/button";
import { QRCodeSVG } from "qrcode.react";
import { setPageSEO } from "../utils/seo";

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [payItForward, setPayItForward] = useState(false);
  const [pifPool, setPifPool] = useState(0);
  const [btcModal, setBtcModal] = useState(null);
  const [btcChecking, setBtcChecking] = useState(false);
  const [btcCopied, setBtcCopied] = useState(false);

  useEffect(() => {
    if (!btcModal?.invoice?.invoice_id) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/btc-pay/check-status/${btcModal.invoice.invoice_id}`);
        if (res.data.status === "paid") {
          clearInterval(interval);
          setBtcModal(null);
          window.location.reload();
        } else if (res.data.status === "expired") {
          clearInterval(interval);
          setBtcModal(null);
          alert("Invoice expired. Please try again.");
        }
      } catch {}
    }, 10000);
    return () => clearInterval(interval);
  }, [btcModal?.invoice?.invoice_id]);

  useEffect(() => {
    document.title = "Pricing — Bitcoin Course One-Time Purchase | Bitcoin Brain";
    setPageSEO({
      title: "Pricing — Bitcoin Course One-Time Purchase | Bitcoin Brain",
      description: "Learn Bitcoin with no subscription. A one-time purchase Bitcoin course built for working families — starting at $21. Pay with card or Lightning sats.",
      path: "/pricing"
    });
    api.get("/payments/pricing").then(res => {
      setTiers(res.data.tiers || []);
      setPifPool(res.data.pay_it_forward_pool || 0);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handlePurchase = async (tierId) => {
    setCheckoutLoading(tierId);
    try {
      const res = await api.post("/payments/checkout/session", {
        tier: tierId,
        origin_url: window.location.origin,
        pay_it_forward: payItForward
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      console.error("Checkout error:", err);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleBtcPurchase = async (tierId) => {
    setCheckoutLoading(tierId);
    try {
      const res = await api.post("/btc-pay/create-invoice", {
        tier: tierId,
        pay_it_forward: payItForward
      });
      setBtcModal({ tier: tierId, invoice: res.data });
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to create BTC invoice";
      alert(msg);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const checkBtcStatus = async () => {
    if (!btcModal?.invoice?.invoice_id) return;
    setBtcChecking(true);
    try {
      const res = await api.get(`/btc-pay/check-status/${btcModal.invoice.invoice_id}`);
      if (res.data.status === "paid") {
        setBtcModal(null);
        window.location.reload();
      } else if (res.data.status === "expired") {
        alert("Invoice expired. Please try again.");
        setBtcModal(null);
      }
    } catch {}
    setBtcChecking(false);
  };

  const copyBtcAddress = () => {
    const toCopy = btcModal?.invoice?.ln_invoice || btcModal?.invoice?.qr_uri || "";
    if (toCopy) {
      navigator.clipboard.writeText(toCopy);
      setBtcCopied(true);
      setTimeout(() => setBtcCopied(false), 2000);
    }
  };

  const isPurchased = user?.purchase_tier && user.purchase_tier !== "free";

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const genesis = tiers.find(t => t.id === "genesis");
  const earlyBeliever = tiers.find(t => t.id === "early_believer");
  const standard = tiers.find(t => t.id === "standard");
  const educator = tiers.find(t => t.id === "educator");
  const genesisSoldOut = genesis?.sold_out;
  const ebActive = earlyBeliever?.active && !earlyBeliever?.sold_out;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Sticky header */}
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/5 rounded-lg" data-testid="back-btn">
            <ArrowLeft className="text-zinc-400" size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">Get Bitcoin Brain</h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Already purchased banner */}
        {isPurchased && (
          <div className="mb-8 p-6 card-dark border-[#F7931A]/30" data-testid="already-purchased">
            <div className="flex items-center gap-3">
              <Check className="text-[#F7931A]" size={24} />
              <div>
                <p className="text-white font-medium">You own Bitcoin Brain</p>
                <p className="text-zinc-500 text-sm capitalize">{user.purchase_tier} tier — Full access unlocked</p>
              </div>
            </div>
          </div>
        )}

        {/* ═══ FIX 1 — HEADER SECTION ═══ */}
        <div className="text-center mb-12" data-testid="pricing-header">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-3">
            Get Bitcoin Brain
          </h2>
          <p className="text-[#F7931A] italic text-sm sm:text-base max-w-2xl mx-auto">
            Built by a general construction apprentice from California. A $50 gift card. Five years of learning. One fair price. Learn Bitcoin — no subscription required.
          </p>
          <p className="text-zinc-500 text-xs sm:text-sm mt-3">
            A Bitcoin course one-time purchase — used by working families and homeschool parents across the US
          </p>
        </div>

        {/* ═══ TIER CARDS ═══ */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="tier-grid">
          {/* ═══ FIX 2 — GENESIS TIER ═══ */}
          {genesis && (
            <GenesisCard
              tier={genesis}
              isPurchased={isPurchased}
              checkoutLoading={checkoutLoading}
              onPurchase={handlePurchase}
              onBtcPurchase={handleBtcPurchase}
            />
          )}

          {/* ═══ FIX 3 — EARLY BELIEVER TIER ═══ */}
          {earlyBeliever && (
            <EarlyBelieverCard
              tier={earlyBeliever}
              genesis={genesis}
              genesisSoldOut={genesisSoldOut}
              isPurchased={isPurchased}
              checkoutLoading={checkoutLoading}
              onPurchase={handlePurchase}
              onBtcPurchase={handleBtcPurchase}
            />
          )}

          {/* ═══ FIX 4 — STANDARD TIER ═══ */}
          {standard && (
            <StandardCard
              tier={standard}
              isPurchased={isPurchased}
              checkoutLoading={checkoutLoading}
              onPurchase={handlePurchase}
              onBtcPurchase={handleBtcPurchase}
            />
          )}

          {/* ═══ FIX 5 — EDUCATOR TIER ═══ */}
          {educator && (
            <EducatorCard
              tier={educator}
              isPurchased={isPurchased}
              checkoutLoading={checkoutLoading}
              onPurchase={handlePurchase}
              onBtcPurchase={handleBtcPurchase}
            />
          )}
        </div>

        {/* ═══ FIX 8 — Orange divider ═══ */}
        {!isPurchased && <div className="w-full h-px bg-[#F7931A]/20 mb-8" />}

        {/* ═══ FIX 6 — FUND A FAMILY (renamed from Pay It Forward) ═══ */}
        {!isPurchased && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-xl mx-auto card-dark p-6 mb-8"
            data-testid="fund-a-family-section"
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={payItForward}
                onChange={(e) => setPayItForward(e.target.checked)}
                className="mt-1 w-5 h-5 accent-[#F7931A] cursor-pointer flex-shrink-0"
                data-testid="faf-checkbox"
              />
              <div>
                <div className="flex items-center gap-2">
                  <Heart className="text-[#F7931A]" size={16} />
                  <span className="text-white font-medium">Add $21 — Fund a Family</span>
                </div>
                <p className="text-zinc-500 text-sm mt-1">
                  Fund Bitcoin Brain access for a working family who cannot afford it. You earn the Fund a Family badge. They get the full course.
                </p>
                {pifPool > 0 && (
                  <p className="text-[#F7931A]/70 text-xs mt-1">${pifPool} in pool helping families</p>
                )}
              </div>
            </label>
            <AnimatePresence>
              {payItForward && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-emerald-400/80 text-xs italic mt-3 ml-8"
                  data-testid="faf-confirmation"
                >
                  You are funding a family. Someone who could not afford this will learn Bitcoin because of you.
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Application link for families who need help */}
        {!isPurchased && (
          <div className="text-center mb-6">
            <button
              onClick={() => navigate("/fund-a-family")}
              className="text-[#F7931A]/60 text-xs hover:text-[#F7931A] transition-colors underline underline-offset-2"
              data-testid="apply-fund-family-link"
            >
              Can't afford this? A community member may have funded your access.
            </button>
          </div>
        )}

        {/* ═══ FIX 8 — FOOTER ═══ */}
        <div className="text-center space-y-1.5 text-zinc-500 text-xs sm:text-sm mt-8 pb-8" data-testid="pricing-footer">
          <p>One time payment. Instant access. No subscription. No recurring charges. Own it forever.</p>
          <p>Questions? <a href="mailto:bitcoinbrain21m@gmail.com" className="text-[#F7931A]/70 hover:text-[#F7931A] transition-colors">bitcoinbrain21m@gmail.com</a></p>
        </div>
      </main>

      {/* ═══ FIX 7 — BTC Payment Modal (with correct conversion) ═══ */}
      <AnimatePresence>
        {btcModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setBtcModal(null)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#0A0A0A] border border-[#F7931A]/30 rounded-2xl p-6 w-full max-w-md"
              onClick={e => e.stopPropagation()} data-testid="btc-payment-modal">
              <div className="text-center mb-4">
                <Bitcoin className="text-[#F7931A] mx-auto mb-2" size={32} />
                <h3 className="font-heading text-xl font-bold text-white">Pay with Bitcoin</h3>
                <p className="text-zinc-500 text-sm">{btcModal.tier.replace("_", " ")} tier</p>
                <p className="text-zinc-600 text-xs mt-1">via Strike Lightning Network</p>
              </div>

              <div className="bg-white rounded-xl p-4 flex justify-center mb-4">
                <QRCodeSVG value={btcModal.invoice.ln_invoice || btcModal.invoice.payment_link || btcModal.invoice.qr_uri} size={200} level="M"
                  fgColor="#000000" bgColor="#ffffff" data-testid="btc-qr-code" />
              </div>

              <div className="space-y-3 mb-4">
                {/* Amount display — FIX 7 */}
                <div className="bg-[#161616] border border-white/10 rounded-lg p-3">
                  <p className="text-zinc-500 text-xs mb-1">Amount</p>
                  <p className="text-[#F7931A] font-mono font-bold text-lg" data-testid="btc-amount-usd">
                    ${btcModal.invoice.amount_usd} USD
                  </p>
                  {btcModal.invoice.btc_amount > 0 && (
                    <div className="mt-1 space-y-0.5">
                      <p className="text-white font-mono text-sm" data-testid="btc-amount-btc">
                        In Bitcoin: {btcModal.invoice.btc_amount.toFixed(8)} BTC
                      </p>
                      {btcModal.invoice.sats_amount > 0 && (
                        <p className="text-zinc-400 font-mono text-sm" data-testid="btc-amount-sats">
                          In sats: ~{btcModal.invoice.sats_amount.toLocaleString()} sats
                        </p>
                      )}
                      <p className="text-zinc-600 text-xs italic mt-1">
                        Exact sat amount updates live with current Bitcoin price
                      </p>
                    </div>
                  )}
                </div>

                {btcModal.invoice.ln_invoice ? (
                  <div className="bg-[#161616] border border-white/10 rounded-lg p-3">
                    <p className="text-zinc-500 text-xs mb-1">Lightning Invoice</p>
                    <div className="flex items-center gap-2">
                      <p className="text-white font-mono text-xs break-all flex-1 line-clamp-2">{btcModal.invoice.ln_invoice}</p>
                      <button onClick={copyBtcAddress} className="text-zinc-400 hover:text-[#F7931A] flex-shrink-0" data-testid="copy-ln-invoice">
                        {btcCopied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>
                ) : btcModal.invoice.payment_link && (
                  <a href={btcModal.invoice.payment_link} target="_blank" rel="noopener noreferrer"
                    className="block bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-lg p-3 text-center hover:bg-[#F7931A]/20 transition-colors"
                    data-testid="strike-payment-link">
                    <p className="text-[#F7931A] font-medium text-sm">Open in Strike</p>
                    <p className="text-zinc-500 text-xs mt-0.5">Pay via Strike app or browser</p>
                  </a>
                )}
                <p className="text-zinc-600 text-xs text-center">
                  {btcModal.invoice.ln_invoice ? "Scan with any Lightning wallet" : "Pay via Strike app or scan QR with a Lightning wallet"}
                </p>
              </div>

              <div className="space-y-2">
                <Button onClick={checkBtcStatus} disabled={btcChecking}
                  className="w-full btn-primary" data-testid="check-btc-status-btn">
                  <RefreshCw size={16} className={`mr-2 ${btcChecking ? "animate-spin" : ""}`} />
                  {btcChecking ? "Checking..." : "Check Payment Status"}
                </Button>
                <Button onClick={() => setBtcModal(null)} variant="ghost"
                  className="w-full text-zinc-500 hover:text-zinc-300">
                  Cancel
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════
   FIX 2 — GENESIS CARD
   ═══════════════════════════════════════════════════════════════ */
function GenesisCard({ tier, isPurchased, checkoutLoading, onPurchase, onBtcPurchase }) {
  const remaining = tier.remaining ?? 0;
  const max = tier.max ?? 21;
  const pctSold = ((max - remaining) / max) * 100;
  const isUrgent = remaining <= 5;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0 }}
      className={`card-dark p-6 flex flex-col relative overflow-hidden genesis-pulse-border ${tier.sold_out ? "opacity-60" : ""}`}
      data-testid="tier-genesis"
    >
      {/* FOUNDING PRICE badge */}
      {!tier.sold_out && (
        <div className="absolute top-3 right-3 bg-[#F7931A] text-black text-[10px] font-bold px-2 py-0.5 rounded tracking-wider" data-testid="founding-price-badge">
          FOUNDING PRICE
        </div>
      )}

      {/* Tier label */}
      <div className="flex items-center gap-2 mb-1">
        <Crown className="text-[#F7931A]" size={20} />
        <span className="text-white font-heading font-bold text-lg">GENESIS</span>
      </div>
      <p className="text-[#F7931A]/70 text-xs mb-4">Founder's Price</p>

      {/* Price */}
      <div className="mb-4">
        <span className="font-mono text-4xl font-bold text-white">${tier.price}</span>
        <span className="text-zinc-500 text-sm ml-1">one time</span>
      </div>

      {/* Slot counter — urgency */}
      {!tier.sold_out && (
        <div className="mb-4" data-testid="genesis-slots">
          <p className={`font-mono text-2xl font-bold mb-1 ${isUrgent ? "text-red-400" : "text-[#F7931A]"}`}>
            {remaining}
          </p>
          <p className="text-zinc-400 text-xs mb-2">of {max} founding slots remaining</p>
          <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${isUrgent ? "bg-red-500" : "bg-[#F7931A]"}`}
              style={{ width: `${pctSold}%` }}
            />
          </div>
          <p className="text-zinc-600 text-[10px] mt-1.5 italic">
            Gone forever when sold out — never offered again
          </p>
        </div>
      )}

      {tier.sold_out && (
        <div className="mb-4 p-2 bg-red-500/10 border border-red-500/30 rounded text-center">
          <span className="text-red-400 text-sm font-bold">SOLD OUT</span>
        </div>
      )}

      {/* Feature list */}
      <ul className="space-y-2 mb-4 flex-grow">
        {tier.features?.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Check className="text-[#F7931A] flex-shrink-0 mt-0.5" size={14} />
            <span className="text-zinc-300">{f}</span>
          </li>
        ))}
      </ul>

      <p className="text-zinc-500 text-[10px] italic mb-4">
        Every founding member is part of the Bitcoin Brain origin story
      </p>

      {/* Buttons */}
      <div className="space-y-2">
        <Button
          onClick={() => onPurchase(tier.id)}
          disabled={isPurchased || tier.sold_out || checkoutLoading === tier.id}
          className={`w-full min-h-[48px] ${isPurchased || tier.sold_out ? "opacity-50" : "bg-[#F7931A] hover:bg-[#F7931A]/90 text-black font-bold"}`}
          data-testid="purchase-genesis-btn"
        >
          {checkoutLoading === tier.id ? "Loading..." :
           isPurchased ? "Already Owned" :
           tier.sold_out ? "Sold Out" :
           `PAY $${tier.price} — FIAT CARD`}
        </Button>
        {!isPurchased && !tier.sold_out && (
          <Button
            onClick={() => onBtcPurchase(tier.id)}
            variant="ghost"
            className="w-full min-h-[48px] border border-[#F7931A]/30 text-[#F7931A] hover:bg-[#F7931A]/10"
            data-testid="btc-purchase-genesis-btn"
          >
            <Bitcoin size={16} className="mr-2" /> Pay in Sats
          </Button>
        )}
      </div>
    </motion.div>
  );
}


/* ═══════════════════════════════════════════════════════════════
   FIX 3 — EARLY BELIEVER CARD
   ═══════════════════════════════════════════════════════════════ */
function EarlyBelieverCard({ tier, genesis, genesisSoldOut, isPurchased, checkoutLoading, onPurchase, onBtcPurchase }) {
  const isActive = tier.active && !tier.sold_out;
  const remaining = tier.remaining ?? 0;
  const max = tier.max ?? 210;
  const pctSold = ((max - remaining) / max) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className={`card-dark p-6 flex flex-col relative overflow-hidden ${
        isActive ? "genesis-pulse-border" : "opacity-75 border-dashed border-[#F7931A]/20"
      } ${tier.sold_out ? "opacity-60" : ""}`}
      data-testid="tier-early_believer"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="text-[#F7931A]" size={20} />
        <span className="text-white font-heading font-bold text-lg">EARLY BELIEVER</span>
      </div>

      {/* Price */}
      <div className="mb-4 mt-2">
        <span className="font-mono text-4xl font-bold text-white">${tier.price}</span>
        <span className="text-zinc-500 text-sm ml-1">one time</span>
      </div>

      {/* Active state — Genesis sold out, slots open */}
      {isActive && (
        <div className="mb-4" data-testid="eb-active-counter">
          {genesisSoldOut && (
            <p className="text-[#F7931A] text-xs font-medium mb-2">
              Genesis sold out — Early Believer now open
            </p>
          )}
          <p className="font-mono text-xl font-bold text-[#F7931A] mb-1">{remaining}</p>
          <p className="text-zinc-400 text-xs mb-2">of {max} early believer slots remaining</p>
          <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-[#F7931A] rounded-full transition-all duration-500" style={{ width: `${pctSold}%` }} />
          </div>
          <p className="text-zinc-600 text-[10px] mt-1.5 italic">
            This price disappears when {max} slots fill — standard price is $49
          </p>
        </div>
      )}

      {/* Locked state */}
      {!isActive && !tier.sold_out && (
        <div className="mb-4 p-3 bg-[#F7931A]/5 border border-[#F7931A]/20 rounded-lg" data-testid="early-believer-locked">
          <p className="text-[#F7931A]/70 text-xs font-medium mb-1">
            Waiting for {genesis?.remaining || 0} founding members — then this unlocks at $42
          </p>
          <div className="flex justify-between text-xs mt-2">
            <span className="text-zinc-500">Genesis slots remaining</span>
            <span className="text-white font-mono">{genesis?.remaining || 0} / {genesis?.max || 21}</span>
          </div>
          <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden mt-1">
            <div className="h-full bg-[#F7931A]/40 rounded-full" style={{ width: `${((genesis?.max - genesis?.remaining) / genesis?.max) * 100}%` }} />
          </div>
        </div>
      )}

      {tier.sold_out && (
        <div className="mb-4 p-2 bg-red-500/10 border border-red-500/30 rounded text-center">
          <span className="text-red-400 text-sm font-bold">SOLD OUT</span>
        </div>
      )}

      {/* Features */}
      <ul className="space-y-2 mb-6 flex-grow">
        {tier.features?.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Check className="text-[#F7931A] flex-shrink-0 mt-0.5" size={14} />
            <span className="text-zinc-300">{f}</span>
          </li>
        ))}
      </ul>

      {/* Buttons */}
      <div className="space-y-2">
        <Button
          onClick={() => onPurchase(tier.id)}
          disabled={isPurchased || tier.sold_out || !isActive || checkoutLoading === tier.id}
          className={`w-full min-h-[48px] ${isPurchased || tier.sold_out || !isActive ? "opacity-50" :
            "bg-[#F7931A] hover:bg-[#F7931A]/90 text-black font-bold"}`}
          data-testid="purchase-early_believer-btn"
        >
          {checkoutLoading === tier.id ? "Loading..." :
           isPurchased ? "Already Owned" :
           tier.sold_out ? "Sold Out" :
           !isActive ? "Unlocks After Genesis" :
           `PAY $${tier.price} — FIAT CARD`}
        </Button>
        {!isPurchased && !tier.sold_out && isActive && (
          <Button
            onClick={() => onBtcPurchase(tier.id)}
            variant="ghost"
            className="w-full min-h-[48px] border border-[#F7931A]/30 text-[#F7931A] hover:bg-[#F7931A]/10"
            data-testid="btc-purchase-early_believer-btn"
          >
            <Bitcoin size={16} className="mr-2" /> Pay in Sats
          </Button>
        )}
      </div>
    </motion.div>
  );
}


/* ═══════════════════════════════════════════════════════════════
   FIX 4 — STANDARD CARD (anchor price, no badge)
   ═══════════════════════════════════════════════════════════════ */
function StandardCard({ tier, isPurchased, checkoutLoading, onPurchase, onBtcPurchase }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className={`card-dark p-6 flex flex-col relative overflow-hidden`}
      data-testid="tier-standard"
    >
      <div className="flex items-center gap-2 mb-1">
        <Crown className="text-[#F7931A]" size={20} />
        <span className="text-white font-heading font-bold text-lg">BITCOIN BRAIN</span>
      </div>

      <p className="text-zinc-500 text-xs mb-3">After founding tiers sell out</p>

      <div className="mb-4">
        <span className="font-mono text-4xl font-bold text-white">${tier.price}</span>
        <span className="text-zinc-500 text-sm ml-1">one time</span>
      </div>

      <ul className="space-y-2 mb-6 flex-grow">
        {tier.features?.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Check className="text-[#F7931A] flex-shrink-0 mt-0.5" size={14} />
            <span className="text-zinc-300">{f}</span>
          </li>
        ))}
      </ul>

      <div className="space-y-2">
        <Button
          onClick={() => onPurchase(tier.id)}
          disabled={isPurchased || checkoutLoading === tier.id}
          className={`w-full min-h-[48px] ${isPurchased ? "opacity-50" : "btn-primary"}`}
          data-testid="purchase-standard-btn"
        >
          {checkoutLoading === tier.id ? "Loading..." :
           isPurchased ? "Already Owned" :
           `PAY $${tier.price} — FIAT CARD`}
        </Button>
        {!isPurchased && (
          <Button
            onClick={() => onBtcPurchase(tier.id)}
            variant="ghost"
            className="w-full min-h-[48px] border border-[#F7931A]/30 text-[#F7931A] hover:bg-[#F7931A]/10"
            data-testid="btc-purchase-standard-btn"
          >
            <Bitcoin size={16} className="mr-2" /> Pay in Sats
          </Button>
        )}
      </div>
    </motion.div>
  );
}


/* ═══════════════════════════════════════════════════════════════
   FIX 5 — EDUCATOR CARD (homeschool angle)
   ═══════════════════════════════════════════════════════════════ */
const STUDENT_TIERS = [
  { id: "edu_4", students: 4, price: 99, label: "4 Students" },
  { id: "edu_8", students: 8, price: 149, label: "8 Students" },
  { id: "edu_12", students: 12, price: 199, label: "12 Students" },
  { id: "edu_unlimited", students: 999, price: 299, label: "Unlimited" },
];

function EducatorCard({ tier, isPurchased, checkoutLoading, onPurchase, onBtcPurchase }) {
  const [selectedTier, setSelectedTier] = useState(STUDENT_TIERS[0]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="card-dark p-6 flex flex-col relative overflow-hidden"
      data-testid="tier-educator"
    >
      {/* HOMESCHOOL FAMILIES badge */}
      <div className="absolute top-3 right-3 border border-[#F7931A] text-[#F7931A] text-[10px] font-bold px-2 py-0.5 rounded tracking-wider" data-testid="homeschool-badge">
        HOMESCHOOL FAMILIES
      </div>

      <div className="flex items-center gap-2 mb-2">
        <GraduationCap className="text-[#F7931A]" size={20} />
        <span className="text-white font-heading font-bold text-lg">EDUCATOR LICENSE</span>
      </div>

      <div className="mb-2">
        <span className="font-mono text-4xl font-bold text-white">${selectedTier.price}</span>
        <span className="text-zinc-500 text-sm ml-1">one time</span>
      </div>

      <p className="text-zinc-400 text-xs mb-3 italic">
        One purchase. Complete sound money curriculum for your students.
      </p>

      {/* Student tier selector */}
      {!isPurchased && (
        <div className="mb-4" data-testid="student-tier-selector">
          <p className="text-zinc-500 text-xs mb-2 font-medium">Student accounts included:</p>
          <div className="grid grid-cols-2 gap-1.5">
            {STUDENT_TIERS.map((st) => (
              <button
                key={st.id}
                onClick={() => setSelectedTier(st)}
                className={`py-2 px-2 rounded-lg text-xs font-medium transition-all border ${
                  selectedTier.id === st.id
                    ? "border-[#F7931A] bg-[#F7931A]/10 text-[#F7931A]"
                    : "border-white/5 bg-[#0A0A0A] text-zinc-400 hover:border-white/20"
                }`}
                data-testid={`student-tier-${st.id}`}
              >
                {st.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <ul className="space-y-2 mb-6 flex-grow">
        {tier.features?.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Check className="text-[#F7931A] flex-shrink-0 mt-0.5" size={14} />
            <span className="text-zinc-300">{f}</span>
          </li>
        ))}
        <li className="flex items-start gap-2 text-sm">
          <Check className="text-[#F7931A] flex-shrink-0 mt-0.5" size={14} />
          <span className="text-zinc-300">
            {selectedTier.students === 999 ? "Unlimited" : `Up to ${selectedTier.students}`} linked student accounts
          </span>
        </li>
      </ul>

      <div className="space-y-2">
        <Button
          onClick={() => onPurchase(selectedTier.id)}
          disabled={isPurchased || checkoutLoading === selectedTier.id}
          className={`w-full min-h-[48px] ${isPurchased ? "opacity-50" : "btn-primary"}`}
          data-testid="purchase-educator-btn"
        >
          {checkoutLoading === selectedTier.id ? "Loading..." :
           isPurchased ? "Already Owned" :
           `PAY $${selectedTier.price} — FIAT CARD`}
        </Button>
        {!isPurchased && (
          <Button
            onClick={() => onBtcPurchase(selectedTier.id)}
            variant="ghost"
            className="w-full min-h-[48px] border border-[#F7931A]/30 text-[#F7931A] hover:bg-[#F7931A]/10"
            data-testid="btc-purchase-educator-btn"
          >
            <Bitcoin size={16} className="mr-2" /> Pay in Sats
          </Button>
        )}
      </div>
    </motion.div>
  );
}
